import { createSimpleAudioBufferPlaybackGraph } from "@/client/core/audio/Audio";
import type {
    CreatePreviewPlayer,
    GetAudioResource,
    PlaybackState,
    PreviewPlayer,
} from "@/client/core/Core.api";
import type { LabsElementaryGraph } from "@/client/core/platform-types/labs.api";

import { createSignal } from "@/client/utils/signal";
import { clamp } from "@/shared/utils/clamp";
import { noop, noopFactory } from "@/shared/utils/noop";

export const kJitterAmount_ms = 50; // for testing only

/** Removes the overlay it was handed out for, unless something has since replaced it. */
export interface OverlayHandle {
    clear: () => void;
}

/**
 * Previews render as an overlay above the app's own graph rather than in place
 * of it, so auditioning a file never tears down what an app is playing.
 */
export type SetOverlayGraph = (graph: LabsElementaryGraph) => OverlayHandle;

export const createPreviewPlayerFactory = (
    getAudioResource: GetAudioResource,
    setOverlayGraph: SetOverlayGraph,
): CreatePreviewPlayer => {
    let cancelPreviewGlobal: (shouldClearOverlay: boolean) => void = noop;

    return (id): PreviewPlayer | undefined => {
        const resource = getAudioResource(id);
        if (resource === undefined) {
            return undefined;
        }

        const signal = createSignal<PlaybackState>();
        const duration_s = resource.metadata.duration_s;
        let isPlaying = false;
        let startTime_ms = 0;
        let currentOffset_s = 0;
        let timeoutId = -1;
        let overlay: OverlayHandle | undefined;
        let cancelPreviewLocal: (shouldClearOverlay: boolean) => void = noopFactory();

        const start = (startOffset_s: number) => {
            cancelPreviewGlobal(false);

            {
                // update elementary graph
                const startOffset_samples = Math.floor(
                    startOffset_s * resource.metadata.sampleRate,
                );
                const graph = createSimpleAudioBufferPlaybackGraph(
                    id.value,
                    resource.metadata.numberOfChannels,
                    startOffset_samples,
                );
                overlay = setOverlayGraph(graph);
            }

            {
                // update state
                startTime_ms = performance.now();
                currentOffset_s = startOffset_s;
                isPlaying = true;
                signal.notify({ isPlaying: true, startOffset_s });
            }

            {
                // playback cancellation
                cancelPreviewGlobal = cancelPreviewLocal = (shouldClearOverlay: boolean) => {
                    clearTimeout(timeoutId);
                    isPlaying = false;
                    signal.notify({ isPlaying: false });
                    // Not conditional on wasPlaying: when a slow render outlasts the file's
                    // nominal duration the UI timer has already flipped that to false, and
                    // the graph is still queued. A later stop has to be able to take it out,
                    // or it renders and sounds after its UI is gone. The handle's ownership
                    // check keeps this from touching a replacement overlay, and dropping the
                    // handle afterwards makes a second stop a no-op.
                    if (shouldClearOverlay) {
                        overlay?.clear();
                        overlay = undefined;
                    }
                    cancelPreviewGlobal = noopFactory();
                };

                // The timer only takes the UI out of its playing state. It cannot remove
                // the overlay: rendering is asynchronous (the renderer serializes work and
                // may still be decoding the file or updating the VFS), so this fires a
                // decode's worth of latency before playback actually ends and would cut it
                // short. A finished one-shot is silent, and the next overlay or an explicit
                // stop takes it out of the mix.
                const remaining_ms = kJitterAmount_ms + (duration_s - startOffset_s) * 1000;
                timeoutId = window.setTimeout(() => cancelPreviewLocal(false), remaining_ms);
            }
        };

        const stop = () => {
            const isCurrentPlayer = cancelPreviewGlobal === cancelPreviewLocal;
            if (isCurrentPlayer) {
                cancelPreviewGlobal(true);
                cancelPreviewGlobal = noopFactory();
            } else {
                cancelPreviewLocal(true);
            }
        };

        const getElapsed_s = () => {
            if (!isPlaying) {
                return 0;
            }
            const elapsed_s = (performance.now() - startTime_ms) / 1000;
            return clamp(currentOffset_s + elapsed_s, 0, duration_s);
        };

        return {
            signal,
            start,
            stop,
            getIsPlaying: () => isPlaying,
            getElapsed_s,
            getDuration_s: () => duration_s,
        };
    };
};
